/**
 * Combine the caller's abort signal with this request's timeout budget.
 *
 * Assign the result to `signal` AFTER the `...options` spread and it silently
 * replaces the caller's — which is how every caller signal in the app came to be
 * dropped, leaving cancellation wired end to end and inert. Keep both.
 *
 * `timeoutMs <= 0` means no abort budget (flavour apply holds the connection
 * until the provisioner finishes). Then only the caller signal is forwarded.
 */
export const combineAbortSignals = (
  callerSignal: AbortSignal | null | undefined,
  timeoutSignal: AbortSignal | undefined,
): AbortSignal | undefined => {
  if (!timeoutSignal) {
    return callerSignal ?? undefined;
  }
  if (!callerSignal) {
    return timeoutSignal;
  }

  return AbortSignal.any([callerSignal, timeoutSignal]);
};

/** `timeoutMs <= 0` disables the abort timer. `setTimeout(..., 0)` would abort immediately. */
export const armRequestTimeout = (
  timeoutMs: number,
): { signal?: AbortSignal; clear: () => void } => {
  if (timeoutMs <= 0) {
    return { clear: () => undefined };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return { signal: controller.signal, clear: () => clearTimeout(timeoutId) };
};
